import { useEffect, useMemo } from 'react';

import { IJournalEvent } from '@entities/eventJournal';
import { DateHelpers } from '@shared/helpers';

import type { IEventJournalModalSchema } from '../../model/types';
import type { UseFormReset } from 'react-hook-form';

interface UseSyncEventJournalModalFormParams {
    isOpen: boolean;
    modalMode: string;
    eventData?: {
        eventDate: string;
        eventItemData: IJournalEvent;
    };
    reset: UseFormReset<IEventJournalModalSchema>;
}

/**
 * Синхронизирует значения формы модалки при открытии/смене режима/данных.
 * Не трогает form state, если модалка закрыта.
 */
export const useSyncEventJournalModalForm = ({
    isOpen,
    modalMode,
    eventData,
    reset,
}: UseSyncEventJournalModalFormParams) => {
    const { getToday, dateFormatter } = DateHelpers;
    const defaultCreateValues = useMemo<IEventJournalModalSchema>(
        () => ({
            selectEstimates: null,
            description: '',
            attachments: [],
            eventDate: dateFormatter({ date: getToday() }),
        }),
        [dateFormatter, getToday]
    );

    const editValues = useMemo<IEventJournalModalSchema | null>(() => {
        if (!eventData?.eventItemData) return null;
        return {
            eventDate: eventData?.eventDate ?? '',
            selectEstimates: eventData?.eventItemData?.estimateId,
            description: eventData?.eventItemData?.description ?? '',
            attachments: eventData?.eventItemData?.attachments ?? [],
        };
    }, [eventData?.eventDate, eventData?.eventItemData]);

    useEffect(() => {
        if (!isOpen) return;

        if (modalMode === 'edit' && editValues) {
            reset(editValues, { keepDirty: false, keepTouched: false });
            return;
        }

        if (modalMode === 'create') {
            reset(defaultCreateValues, { keepDirty: false, keepTouched: false });
        }
    }, [isOpen, modalMode, editValues, defaultCreateValues, reset]);
};
